import { type searchHistoryItem } from '../types';
import { type ReserveType } from '@utils/itinerary';

export class SearchHistory {
    public allSearchHistories: Partial<
        Record<ReserveType, searchHistoryItem[]>
    > = {};

    private get searchHistoryStorageKey() {
        const id = useAuthStore().userInfo?.id;
        if (id) {
            return `${id}-search-history`;
        }
        return null;
    }

    private validateSearchHistoryItem(searchHistoryItems: searchHistoryItem[]) {
        return searchHistoryItems.filter(
            ({ departureDate, expireDate }) =>
                dayConfig.gregory().isSameOrBefore(departureDate) &&
                dayConfig
                    .gregory(expireDate)
                    .add(30, 'day')
                    .isAfter(dayConfig.gregory())
        );
    }

    addSearchHistoryItem(
        item: Omit<searchHistoryItem, 'expireDate'>,
        reserveType: ReserveType
    ) {
        if (!this.searchHistoryStorageKey) {
            return;
        }

        const searchHistory = this.allSearchHistories[reserveType];
        const searchHistoryItems = [...(searchHistory || [])];
        if (searchHistory?.length) {
            const notExpiredIndex = searchHistory.findIndex(
                ({ expireDate, destinationName, originName }) =>
                    destinationName === item.destinationName &&
                    originName === item.originName &&
                    dayConfig.gregory().isSameOrBefore(expireDate)
            );
            if (notExpiredIndex >= 0) {
                searchHistoryItems.splice(notExpiredIndex, 1);
            }
        }

        this.allSearchHistories[reserveType] = [
            {
                ...item,
                expireDate: dayConfig.gregory().add(15, 'minute').toISOString(),
            },
            ...(searchHistoryItems || []),
        ].slice(0, 5);
        this.saveSearchHistory();
    }

    init() {
        if (this.searchHistoryStorageKey) {
            const val =
                LocalStorageUtils.getItem<
                    Partial<Record<ReserveType, searchHistoryItem[]>>
                >(this.searchHistoryStorageKey) || {};
            Object.keys(val).forEach(key => {
                this.allSearchHistories[key as ReserveType] =
                    this.validateSearchHistoryItem(
                        val?.[key as ReserveType] || []
                    );
            });
            this.saveSearchHistory();
        }
    }

    saveSearchHistory() {
        if (this.searchHistoryStorageKey) {
            LocalStorageUtils.setItem(
                this.searchHistoryStorageKey,
                this.allSearchHistories
            );
        }
    }

    clearSearchHistory(reserveType: ReserveType) {
        this.allSearchHistories[reserveType] = [];
        this.saveSearchHistory();
    }

    clearAllHistory() {
        this.allSearchHistories = {};
    }

    toJSON() {
        return { ...this };
    }
}
